import { Route, Outlet } from 'react-router-dom';
import { type RouteRegistryEntry } from 'FrontRoyalRouteRegistry/FrontRoyalRouteRegistry.types';
import { ProtectedRoute } from 'ProtectedRoute';
import { type CurrentUser, hasSuperEditorAccess, hasEditorAccess } from 'Users';
import { EditorIndexComponent } from './components/EditorIndexComponent';
import { EditLessonComponent } from './components/EditLessonComponent';
import { LessonDiffComponent } from './components/LessonDiffComponent';
import { EditStreamComponent } from './components/EditStreamComponent';
import { EditPlaylistComponent } from './components/EditPlaylistComponent';
import { PreviewLessonComponent } from './components/PreviewLessonComponent';

export const EDITOR_ROUTES = {
    EDITOR_INDEX: { path: '/editor' },
    EDIT_LESSON: { path: '/editor/lesson/:lessonId/edit' },
    LESSON_DIFF: { path: '/editor/lesson/:lessonId/diff/:versionId1/:versionId2' },
    EDIT_STREAM: { path: '/editor/course/:streamId/edit' },
    EDIT_PLAYLIST: { path: '/editor/playlist/:playlistId/edit' },
    PREVIEW_LESSON: { path: '/editor/lesson/preview' },
    // Keep this a literal. LessonGenerationPortal exports the same path, but a static import of
    // that barrel would pull MUI and its api slice into the editor chunk.
    LESSON_GENERATIONS: { path: '/editor/lesson-generations' },
};

// Wrapper component that applies the 'editor' class to app-root
function EditorRoutesWrapper() {
    return (
        <div className="editor">
            <Outlet />
        </div>
    );
}

function userHasSuperEditorAccess({ currentUser }: { currentUser: CurrentUser }) {
    return hasSuperEditorAccess(currentUser);
}

function userHasEditorAccess({ currentUser }: { currentUser: CurrentUser }) {
    return hasEditorAccess(currentUser);
}

// Lazy, like the Grading Portal routes, so the MUI portal loads only when an editor opens it.
async function getLazyLessonGenerationPortal() {
    const { LessonGenerationPortal } = await import(/* webpackPrefetch: true */ 'LessonGenerationPortal');
    return {
        Component: () => (
            <ProtectedRoute accessPolicy="custom" accessPolicyFn={userHasEditorAccess}>
                <LessonGenerationPortal />
            </ProtectedRoute>
        ),
    };
}

export function configureEditorRoutes(): RouteRegistryEntry[] {
    const { EDIT_LESSON, PREVIEW_LESSON, ...otherRoutes } = EDITOR_ROUTES;

    return [
        {
            routes: { EDIT_LESSON },
            config: {
                headerLayout: 'lesson-editor',
                errorHandlingRules: 'editor',
            },
        },
        {
            routes: { PREVIEW_LESSON },
            config: {
                headerLayout: 'lesson-player',
            },
        },
        {
            routes: otherRoutes,
            config: {
                errorHandlingRules: 'editor',
            },
        },
    ];
}

export function getEditorRoutes() {
    return (
        <Route id="editor-routes" element={<EditorRoutesWrapper />}>
            <Route
                path={EDITOR_ROUTES.EDITOR_INDEX.path}
                element={
                    <ProtectedRoute accessPolicy="custom" accessPolicyFn={userHasEditorAccess}>
                        <EditorIndexComponent />
                    </ProtectedRoute>
                }
            />
            <Route
                path={EDITOR_ROUTES.EDIT_LESSON.path}
                element={
                    <ProtectedRoute accessPolicy="custom" accessPolicyFn={userHasEditorAccess}>
                        <EditLessonComponent />
                    </ProtectedRoute>
                }
            />
            <Route
                path={EDITOR_ROUTES.LESSON_DIFF.path}
                element={
                    <ProtectedRoute accessPolicy="custom" accessPolicyFn={userHasEditorAccess}>
                        <LessonDiffComponent />
                    </ProtectedRoute>
                }
            />
            <Route
                path={EDITOR_ROUTES.EDIT_STREAM.path}
                element={
                    <ProtectedRoute accessPolicy="custom" accessPolicyFn={userHasSuperEditorAccess}>
                        <EditStreamComponent />
                    </ProtectedRoute>
                }
            />
            <Route
                path={EDITOR_ROUTES.EDIT_PLAYLIST.path}
                element={
                    <ProtectedRoute accessPolicy="custom" accessPolicyFn={userHasSuperEditorAccess}>
                        <EditPlaylistComponent />
                    </ProtectedRoute>
                }
            />
            <Route
                path={EDITOR_ROUTES.PREVIEW_LESSON.path}
                element={
                    <ProtectedRoute accessPolicy="custom" accessPolicyFn={userHasEditorAccess}>
                        <PreviewLessonComponent />
                    </ProtectedRoute>
                }
            />
            <Route path={EDITOR_ROUTES.LESSON_GENERATIONS.path} lazy={getLazyLessonGenerationPortal} />
        </Route>
    );
}
